import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { searchStudentsForPayment } from "../api/studentPaymentsApi";
import {
  getStudentRegistrationPreview,
  getTournamentsForVolunteerRegistration,
  submitTournamentRegistration,
} from "../api/tournamentRegistrationApi";
import { getCsrfToken } from "../../../public/api/authApi";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import GlobalFeedback from "../../../global/GlobalFeedback";
import { GlobalModal } from "../../../global/GlobalModal";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import PaymentStrip from "../../payment/PaymentStrip";
import {
  clearOnlinePaymentSession,
  clearPhonePePaymentResult,
  clearTournamentPaymentSession,
  clearTournamentRegistrationFormDraft,
  getPhonePePaymentResult,
  getTournamentRegistrationFormDraft,
  isActivePhonePePayment,
  markPhonePeOrderConsumed,
  setTournamentRegistrationFormDraft,
} from "../../payment/paymentSession";
import TournamentRegistrationLabelFiltersSection from "../components/registration/sections/TournamentRegistrationLabelFiltersSection";
import TournamentRegistrationTournamentsListSection from "../components/registration/sections/TournamentRegistrationTournamentsListSection";
import TournamentRegistrationFormSection from "../components/registration/sections/TournamentRegistrationFormSection";
import {
  SUGGEST_DEBOUNCE_MS,
  TRANSACTION_ID_MAX_LENGTH,
  selectedStudentDisplayLabel,
  tournamentStatusByRegistrationClosingDate,
} from "../components/registration/tournamentRegistrationHelpers";
import "../styles/studentSearch.css";
import "../styles/tournamentRegistration.css";

export default function TournamentRegistrationPage() {
  const [tournaments, setTournaments] = useState([]);
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [selectedTournament, setSelectedTournament] = useState(null);

  const [studentInput, setStudentInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [studentId, setStudentId] = useState("");
  const [studentCode, setStudentCode] = useState("");
  const [studentName, setStudentName] = useState("");
  const [membershipLabel, setMembershipLabel] = useState("");
  const [verifiedMemberships, setVerifiedMemberships] = useState([]);
  const [selectedMembershipId, setSelectedMembershipId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [paymentType, setPaymentType] = useState("offline");
  const [paymentTransactionId, setPaymentTransactionId] = useState("");
  const [paymentProof, setPaymentProof] = useState("");
  const [paymentProofFileName, setPaymentProofFileName] = useState("");

  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [duplicateModal, setDuplicateModal] = useState(null);
  const [phonePeCompleted, setPhonePeCompleted] = useState(() => {
    const phonePe = getPhonePePaymentResult();
    return isActivePhonePePayment(phonePe) ? phonePe : null;
  });
  const [phonePeError, setPhonePeError] = useState("");
  /** 1 = tournament, 2 = student, 3 = payment */
  const [formStep, setFormStep] = useState(1);

  const suggestTimerRef = useRef(null);
  const typeaheadRef = useRef(null);
  const formPanelRef = useRef(null);
  const registrationFormRef = useRef(null);
  const cardsWrapRef = useRef(null);
  const panelRef = useRef(null);

  /** Refetch tournament cards (e.g. open slots) without blanking the whole list */
  const refreshTournamentsQuietly = useCallback(async () => {
    try {
      const tRes = await getTournamentsForVolunteerRegistration();
      setTournaments(tRes.tournaments || []);
    } catch {
      /* keep existing list; user can refresh page if needed */
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoadingTournaments(true);
      setLoadError("");
      try {
        const tRes = await getTournamentsForVolunteerRegistration();
        setTournaments(tRes.tournaments || []);
      } catch (e) {
        setLoadError(e.message || "Failed to load tournaments");
      } finally {
        setLoadingTournaments(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const phonePe = getPhonePePaymentResult();
    const activePhonePe = isActivePhonePePayment(phonePe) ? phonePe : null;
    const draft = getTournamentRegistrationFormDraft();
    if (!draft && !activePhonePe) return;

    (async () => {
      if (draft?.tournament_id && tournaments.length) {
        const t = tournaments.find((row) => String(row.id) === String(draft.tournament_id));
        if (t) setSelectedTournament(t);
      }
      if (draft?.student_id) {
        setStudentId(String(draft.student_id));
        setStudentCode(draft.studentCode || "");
        setStudentName(draft.studentName || "");
        setStudentInput(draft.studentInput || draft.studentName || "");
        setMembershipLabel(draft.membershipLabel || "");
        setVerifiedMemberships(Array.isArray(draft.verifiedMemberships) ? draft.verifiedMemberships : []);
        setSelectedMembershipId(draft.selectedMembershipId || "");
        if (draft.selectedStudent) setSelectedStudent(draft.selectedStudent);
      }
      if (draft?.payment_type || activePhonePe) {
        setPaymentType(activePhonePe || draft?.payment_type === "online" ? "online" : "offline");
      }
      if (draft?.payment_transaction_id) {
        setPaymentTransactionId(draft.payment_transaction_id);
      }
      if (draft?.payment_proof) setPaymentProof(draft.payment_proof);
      if (draft?.payment_proof_file_name) setPaymentProofFileName(draft.payment_proof_file_name);
      if (draft?.formStep) setFormStep(Number(draft.formStep) || 1);
      if (activePhonePe) setPhonePeCompleted(activePhonePe);
    })();
  }, [tournaments]);

  useEffect(() => {
    const onDocDown = (e) => {
      if (typeaheadRef.current && !typeaheadRef.current.contains(e.target)) {
        setSuggestionsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  useEffect(() => {
    if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    const q = studentInput.trim();
    if (q.length < 1) {
      setSearchResults([]);
      setSuggestLoading(false);
      setSuggestionsOpen(false);
      return;
    }
    if (selectedStudent && q === selectedStudentDisplayLabel(selectedStudent)) {
      setSearchResults([]);
      setSuggestLoading(false);
      setSuggestionsOpen(false);
      return;
    }

    suggestTimerRef.current = setTimeout(async () => {
      setSuggestLoading(true);
      setSuggestionsOpen(true);
      try {
        const result = await searchStudentsForPayment(q);
        setSearchResults(result.students || []);
      } catch {
        setSearchResults([]);
        setFormError("Student search failed");
      } finally {
        setSuggestLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);

    return () => {
      if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    };
  }, [studentInput, selectedStudent]);

  useEffect(() => {
    if (formStep === 3 && !studentId) {
      setFormStep(2);
    }
  }, [studentId, formStep]);

  useEffect(() => {
    const wrap = cardsWrapRef.current;
    const panel = panelRef.current;
    if (!wrap || !panel) return undefined;

    const mq = window.matchMedia ? window.matchMedia("(min-width: 900px)") : null;
    const applySizing = () => {
      const isDesktop = mq ? mq.matches : window.innerWidth >= 900;
      if (!isDesktop) {
        wrap.style.maxHeight = "";
        wrap.style.overflowY = "";
        return;
      }
      const h = panel.getBoundingClientRect().height;
      if (Number.isFinite(h) && h > 0) {
        wrap.style.maxHeight = `${Math.round(h)}px`;
        wrap.style.overflowY = "auto";
      }
    };

    applySizing();

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => applySizing());
      ro.observe(panel);
    }
    const onResize = () => applySizing();
    window.addEventListener("resize", onResize);
    if (mq && mq.addEventListener) mq.addEventListener("change", applySizing);

    return () => {
      window.removeEventListener("resize", onResize);
      if (mq && mq.removeEventListener) mq.removeEventListener("change", applySizing);
      if (ro) ro.disconnect();
      wrap.style.maxHeight = "";
      wrap.style.overflowY = "";
    };
  }, []);

  const handleStudentInputChange = (v) => {
    setStudentInput(v);
    if (selectedStudent) {
      setSelectedStudent(null);
      setStudentId("");
      setStudentCode("");
      setStudentName("");
      setMembershipLabel("");
      setVerifiedMemberships([]);
      setSelectedMembershipId("");
    }
    if (fieldErrors.student_id) {
      setFieldErrors((prev) => ({ ...prev, student_id: undefined }));
    }
  };

  const handleSelectStudent = async (student) => {
    setSelectedStudent(student);
    setStudentInput(selectedStudentDisplayLabel(student));
    setSearchResults([]);
    setSuggestionsOpen(false);
    setFormError("");
    setFieldErrors((prev) => ({ ...prev, student_id: undefined }));

    try {
      const preview = await getStudentRegistrationPreview(student.id);
      const s = preview.student;
      if (s) {
        setStudentId(String(s.id));
        setStudentCode(s.code || "");
        setStudentName(s.full_name || "");
        const memberships = Array.isArray(s.memberships) ? s.memberships : [];
        const safeMemberships = memberships
          .filter((m) => Number(m.membership_id) > 0)
          .map((m) => ({
            membership_id: String(m.membership_id),
            membership_name: m.membership_name || "Membership",
          }));
        setVerifiedMemberships(safeMemberships);
        if (safeMemberships.length > 0) {
          setSelectedMembershipId(safeMemberships[0].membership_id);
          setMembershipLabel(safeMemberships[0].membership_name);
        } else {
          setSelectedMembershipId("");
          setMembershipLabel(s.membership_name || "");
        }
      }
    } catch {
      setStudentId(String(student.id));
      setStudentCode(student.code || "");
      setStudentName(student.full_name || "");
      setMembershipLabel("");
      setVerifiedMemberships([]);
      setSelectedMembershipId("");
    }
  };

  const handleRegisterClick = (t) => {
    setSelectedTournament(t);
    setFormStep(1);
    setStudentInput("");
    setSelectedStudent(null);
    setStudentId("");
    setStudentCode("");
    setStudentName("");
    setMembershipLabel("");
    setVerifiedMemberships([]);
    setSelectedMembershipId("");
    setPaymentType("offline");
    setPaymentTransactionId("");
    setPaymentProof("");
    setPaymentProofFileName("");
    setPhonePeCompleted(null);
    setPhonePeError("");
    clearOnlinePaymentSession();
    clearTournamentRegistrationFormDraft();
    setFormError("");
    setFieldErrors({});
    requestAnimationFrame(() => {
      formPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const clearPaymentSubmitFeedback = () => {
    setFormError("");
    setFieldErrors((prev) => {
      if (!prev || typeof prev !== "object") return {};
      const next = { ...prev };
      delete next.payment_transaction_id;
      delete next.transaction_id;
      delete next.phonepe;
      delete next.proof;
      return next;
    });
  };

  /** After a failed submit, going back to the student step must clear the locked student or the old id can be sent again. */
  const handleBackFromPaymentStep = () => {
    if (formError) {
      setStudentInput("");
      setSelectedStudent(null);
      setStudentId("");
      setStudentCode("");
      setStudentName("");
      setMembershipLabel("");
      setFieldErrors({});
    }
    setFormError("");
    setFormStep(2);
  };

  const conflictHint = (conflict) => {
    if (conflict === "duplicate_receipt") {
      return " Another row already uses this receipt number (receipts are unique for every payment).";
    }
    if (conflict === "duplicate_transaction") {
      return " Another row already uses this transaction id (ids are unique for every payment).";
    }
    if (conflict === "already_registered") {
      return " Already registered.";
    }
    if (conflict === "duplicate_key") {
      return " Check server logs for the duplicate key detail.";
    }
    if (conflict === "legacy_unique_tournament_id") {
      return "";
    }
    return "";
  };

  const resetAfterSuccess = () => {
    setSelectedTournament(null);
    setFormStep(1);
    setStudentInput("");
    setSelectedStudent(null);
    setStudentId("");
    setStudentCode("");
    setStudentName("");
    setMembershipLabel("");
    setVerifiedMemberships([]);
    setSelectedMembershipId("");
    setPaymentType("offline");
    setPaymentTransactionId("");
    setPaymentProof("");
    setPaymentProofFileName("");
    setPhonePeCompleted(null);
    setPhonePeError("");
    clearOnlinePaymentSession();
    clearTournamentPaymentSession();
    setFormError("");
    setFieldErrors({});
  };

  const validatePaymentStep = () => {
    const phonePe = getPhonePePaymentResult();
    const payingOnline = paymentType === "online" || isActivePhonePePayment(phonePe);
    if (!payingOnline) {
      const ref = String(paymentTransactionId || "").trim();
      if (!ref) return { payment_transaction_id: "Receipt number is required" };
      if (ref.length > TRANSACTION_ID_MAX_LENGTH) {
        return {
          payment_transaction_id: `Receipt number must be at most ${TRANSACTION_ID_MAX_LENGTH} characters`,
        };
      }
    } else if (!isActivePhonePePayment(phonePe)) {
      const hasProof = Boolean(String(paymentProof || "").trim());
      if (!hasProof) {
        return { phonepe: "Complete online payment or upload payment proof before submitting" };
      }
    }
    if (!String(paymentProof || "").trim()) {
      return { proof: "Upload payment proof before submitting" };
    }
    return {};
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFieldErrors({});

    if (!selectedTournament) {
      setFormError("Select a tournament from the list first.");
      return;
    }
    const requiresPayment = Number(selectedTournament?.participation_fee ?? 0) > 0;
    if (requiresPayment && formStep < 3) {
      setFormError("Complete each step in order before submitting.");
      return;
    }
    if (!requiresPayment && formStep < 2) {
      setFormError("Complete each step in order before submitting.");
      return;
    }
    if (!studentId) {
      setFieldErrors({ student_id: "Search and select a student" });
      return;
    }

    if (requiresPayment) {
      const paymentErrors = validatePaymentStep();
      if (Object.keys(paymentErrors).length) {
        setFieldErrors(paymentErrors);
        setFormError("Please check the highlighted payment fields.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const csrf = await getCsrfToken();
      const phonePe = getPhonePePaymentResult();
      const resolvedPaymentType =
        paymentType === "online" || isActivePhonePePayment(phonePe) ? "online" : "offline";
      const payload = {
        student_id: Number(studentId),
        tournament_id: selectedTournament.id,
      };
      if (requiresPayment) {
        payload.payment = {
          payment_type: resolvedPaymentType,
          transaction_id:
            resolvedPaymentType === "offline"
              ? String(paymentTransactionId || "").trim()
              : undefined,
          phonepe: resolvedPaymentType === "online" ? phonePe : undefined,
          proof: paymentProof.trim() || null,
        };
      }
      const result = await submitTournamentRegistration(payload, csrf);
      if (phonePe?.orderId) markPhonePeOrderConsumed(phonePe.orderId);
      setSuccessMsg(
        result?.message?.trim() ||
          "Registration submitted and confirmation email sent successfully.",
      );
      await refreshTournamentsQuietly();
      resetAfterSuccess();
    } catch (err) {
      const conflict =
        err.details && typeof err.details === "object" ? err.details.conflict : null;
      if (err.details && typeof err.details === "object") {
        const rest = { ...err.details };
        delete rest.conflict;
        if (Object.keys(rest).length) {
          const mapped = { ...rest };
          if (mapped.transaction_id && !mapped.payment_transaction_id) {
            mapped.payment_transaction_id = mapped.transaction_id;
          }
          setFieldErrors(mapped);
        } else setFieldErrors({});
      }
      const base = err.message || "Could not submit registration";
      if (conflict === "already_registered") {
        setFormError("");
        setDuplicateModal({
          title: "Already registered",
          message:
            "This student is already registered for the selected tournament. Choose another student or tournament.",
        });
      } else if (
        conflict === "duplicate_receipt" ||
        conflict === "duplicate_transaction" ||
        conflict === "duplicate_key" ||
        conflict === "legacy_unique_tournament_id"
      ) {
        setFormError("");
        setDuplicateModal({
          title: "Registration blocked",
          message: base + conflictHint(conflict),
        });
      } else {
        setFormError(base + (conflict ? conflictHint(conflict) : ""));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const step1Complete = !!selectedTournament;
  const step2Complete = !!studentId;
  const requiresPayment = Number(selectedTournament?.participation_fee ?? 0) > 0;
  const canSubmitAtCurrentStep = requiresPayment ? formStep >= 3 : formStep >= 2;
  const submitAmount = Number(selectedTournament?.participation_fee ?? 0);
  const toggleStatusFilter = (next) => {
    setStatusFilter((curr) => (curr === next ? "all" : next));
  };
  const displayedTournaments = useMemo(() => {
    if (!Array.isArray(tournaments)) return [];
    return tournaments.filter((t) => {
      if (statusFilter === "all") return true;
      return tournamentStatusByRegistrationClosingDate(t).key === statusFilter;
    });
  }, [tournaments, statusFilter]);

  return (
    <div className="treg-page">
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />

      <GlobalModal
        open={Boolean(duplicateModal)}
        onClose={() => setDuplicateModal(null)}
        title={duplicateModal?.title || "Registration blocked"}
        footer={
          <GlobalButton type="button" variant="primary" onClick={() => setDuplicateModal(null)}>
            OK
          </GlobalButton>
        }
      >
        <p style={{ margin: 0 }}>{duplicateModal?.message}</p>
      </GlobalModal>

      <header className="treg-hero">
        <p className="treg-hero__eyebrow">Tournaments</p>
        <h2 className="treg-hero__title">Tournament registration</h2>
        <p className="treg-hero__sub">
          Choose a tournament, then work through the steps: confirm the event,
          select the student, then pay offline or online via PhonePe if required. Registration stays pending until
          verified in finance.
        </p>
      </header>

      {loadError ? (
        <GlobalFeedback type="error" text={loadError} />
      ) : null}

      <div className="treg-main">
        <TournamentRegistrationTournamentsListSection
          cardsWrapRef={cardsWrapRef}
          loadingTournaments={loadingTournaments}
          displayedTournaments={displayedTournaments}
          labelFiltersSlot={
            <TournamentRegistrationLabelFiltersSection
              statusFilter={statusFilter}
              onFilterAll={() => {
                setStatusFilter("all");
                resetAfterSuccess();
              }}
              onToggleFilter={toggleStatusFilter}
            />
          }
          onRegisterClick={handleRegisterClick}
        />

        <TournamentRegistrationFormSection
          panelRef={(el) => {
            formPanelRef.current = el;
            panelRef.current = el;
          }}
          registrationFormRef={registrationFormRef}
          formStep={formStep}
          setFormStep={setFormStep}
          step1Complete={step1Complete}
          step2Complete={step2Complete}
          requiresPayment={requiresPayment}
          selectedTournament={selectedTournament}
          studentInput={studentInput}
          handleStudentInputChange={handleStudentInputChange}
          fieldErrors={fieldErrors}
          searchResults={searchResults}
          suggestLoading={suggestLoading}
          suggestionsOpen={suggestionsOpen}
          typeaheadRef={typeaheadRef}
          handleSelectStudent={handleSelectStudent}
          studentCode={studentCode}
          studentName={studentName}
          verifiedMemberships={verifiedMemberships}
          selectedMembershipId={selectedMembershipId}
          setSelectedMembershipId={setSelectedMembershipId}
          setMembershipLabel={setMembershipLabel}
          membershipLabel={membershipLabel}
          studentId={studentId}
          paymentType={isActivePhonePePayment(phonePeCompleted) ? "online" : paymentType}
          paymentTransactionId={paymentTransactionId}
          onPaymentTypeChange={(type) => {
            setPaymentType(type);
            setPhonePeError("");
            if (type === "online") {
              setPaymentTransactionId("");
              if (fieldErrors.payment_transaction_id) {
                setFieldErrors((prev) => ({ ...prev, payment_transaction_id: "" }));
              }
            } else if (fieldErrors.phonepe) {
              setFieldErrors((prev) => ({ ...prev, phonepe: "" }));
            }
          }}
          onPaymentTransactionIdChange={(v) => {
            setPaymentTransactionId(v);
            clearPaymentSubmitFeedback();
          }}
          phonePeCompleted={phonePeCompleted}
          phonePeError={phonePeError}
          onPhonePeError={setPhonePeError}
          onBeforePayRedirect={() => {
            clearPhonePePaymentResult();
            setPhonePeCompleted(null);
            setTournamentRegistrationFormDraft({
              tournament_id: selectedTournament?.id,
              student_id: studentId,
              studentCode,
              studentName,
              studentInput,
              selectedStudent,
              membershipLabel,
              verifiedMemberships,
              selectedMembershipId,
              payment_type: "online",
              payment_transaction_id: paymentTransactionId,
              payment_proof: paymentProof,
              payment_proof_file_name: paymentProofFileName,
              formStep: 3,
            });
          }}
          paymentProof={paymentProof}
          paymentProofFileName={paymentProofFileName}
          onPaymentProofChange={({ url, fileName }) => {
            setPaymentProof(url || "");
            setPaymentProofFileName(fileName || "");
            clearPaymentSubmitFeedback();
          }}
          onClearOnlinePayment={() => {
            clearOnlinePaymentSession();
            clearTournamentRegistrationFormDraft();
            setPhonePeCompleted(null);
            setPhonePeError("");
            setFieldErrors((prev) => ({ ...prev, phonepe: "" }));
          }}
          formError={formError}
          handleSubmit={handleSubmit}
          handleBackFromPaymentStep={handleBackFromPaymentStep}
          canSubmitAtCurrentStep={canSubmitAtCurrentStep}
          submitting={submitting}
          submitAmount={submitAmount}
        />
      </div>
    </div>
  );
}
